import { useEffect, useRef, useState } from 'react';
import { PRICING_ITEMS, STORE_INFO } from '@/mocks/content';

const PAYMENT_INFO_ROWS = [
  { label: '支払方法', value: STORE_INFO.paymentMethods },
  { label: 'TAX', value: STORE_INFO.tax },
  { label: 'サービス料', value: STORE_INFO.serviceCharge },
  { label: 'カード手数料', value: STORE_INFO.cardFee },
  { label: '電子決済手数料', value: STORE_INFO.electronicPaymentFee },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function Pricing() {
  const { ref, inView } = useInView(0.1);
  const sectionRef = useRef<HTMLElement>(null);
  const rightViewportRef = useRef<HTMLDivElement>(null);
  const rightContentRef = useRef<HTMLDivElement>(null);
  const [scrollRange, setScrollRange] = useState(0);
  const [hijackProgress, setHijackProgress] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (window.innerWidth < 1024) {
        setScrollRange(0);
        setHijackProgress(0);
        return;
      }
      const viewportEl = rightViewportRef.current;
      const contentEl = rightContentRef.current;
      if (!viewportEl || !contentEl) return;

      const nextRange = Math.max(0, contentEl.scrollHeight - viewportEl.clientHeight);
      setScrollRange(nextRange);
    };

    const onScroll = () => {
      if (window.innerWidth < 1024) return;
      const sectionEl = sectionRef.current;
      if (!sectionEl) return;

      const topOffset = 112;
      const sectionTop = window.scrollY + sectionEl.getBoundingClientRect().top;
      const start = sectionTop - topOffset;
      const nextProgress = Math.min(Math.max(window.scrollY - start, 0), scrollRange);
      setHijackProgress(nextProgress);
    };

    measure();
    onScroll();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', onScroll);
    };
  }, [scrollRange]);

  useEffect(() => {
    const t = setTimeout(() => {
      const viewportEl = rightViewportRef.current;
      const contentEl = rightContentRef.current;
      if (!viewportEl || !contentEl || window.innerWidth < 1024) return;
      const nextRange = Math.max(0, contentEl.scrollHeight - viewportEl.clientHeight);
      setScrollRange(nextRange);
    }, 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="relative bg-[#0A0A0F] py-24 md:py-36 overflow-hidden"
      style={{
        minHeight: scrollRange > 0 ? `calc(100vh + ${scrollRange}px + 8rem)` : undefined,
      }}
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left column */}
          <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <p
              className="text-[#C9A84C] text-xs tracking-[0.6em] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              SYSTEM &amp; PRICE
            </p>
            <h2
              className="text-white text-3xl md:text-4xl font-bold tracking-widest"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              ご利用案内
            </h2>
            <div className="flex items-center gap-4 mt-6">
              <span
                className="h-px bg-[#C9A84C]/40 transition-all duration-1000"
                style={{ width: inView ? '60px' : '0px' }}
              />
              <i className="ri-star-line text-[#C9A84C]/60 text-xs" />
              <span
                className="h-px bg-[#C9A84C]/40 transition-all duration-1000"
                style={{ width: inView ? '60px' : '0px' }}
              />
            </div>
            <p
              className="text-white/60 text-sm leading-8 mt-8"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              シーンに合わせて選べる各プランをご用意しております。ご予算やご要望に応じたご提案も可能です。
            </p>
            <div className="mt-8 border border-[#C9A84C]/20 rounded-lg p-5 bg-[#C9A84C]/5 space-y-4">
              {PAYMENT_INFO_ROWS.map((row) => (
                <div key={row.label} className="space-y-1">
                  <p
                    className="text-[#C9A84C]/70 text-xs tracking-widest"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {row.label}
                  </p>
                  <p
                    className="text-white/70 text-xs tracking-wide leading-[1.8] break-words"
                    style={{ fontFamily: "'Shippori Mincho', serif" }}
                  >
                    {row.value}
                  </p>
                </div>
              ))}
            </div>
            <p
              className="text-white/30 text-xs mt-5 tracking-widest"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              ご不明な点はお気軽にお電話にてお問い合わせください
            </p>
          </div>

          {/* Right column */}
          <div ref={rightViewportRef} className="lg:col-span-8 min-w-0 lg:h-[calc(100vh-9rem)] lg:overflow-hidden">
            <div
              ref={rightContentRef}
              className="will-change-transform border-y border-[#C9A84C]/25 min-w-0"
              style={{ transform: `translateY(-${hijackProgress}px)` }}
            >
              {PRICING_ITEMS.map((item, i) => (
                <div
                  key={i}
                  className="group relative border-b border-[#C9A84C]/20 px-2 py-6 md:px-4 md:py-7 transition-colors duration-300 hover:bg-[#C9A84C]/5 cursor-default min-w-0"
                  style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? 'translateY(0)' : 'translateY(30px)',
                    transition: `opacity 0.7s ease ${i * 80}ms, transform 0.7s ease ${i * 80}ms, background 0.3s`,
                  }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_12rem] xl:grid-cols-[minmax(0,1fr)_14rem] gap-4 lg:gap-6 lg:items-start">
                    <div className="flex items-start gap-3 md:gap-4 min-w-0">
                      <div className="w-9 h-9 flex items-center justify-center mt-0.5 border border-[#C9A84C]/30 rounded-full flex-shrink-0">
                        <i className={`${item.icon} text-base text-[#C9A84C] group-hover:text-[#E8C96A] transition-colors duration-300`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3
                          className="text-white text-base md:text-lg font-bold tracking-wide mb-2"
                          style={{ fontFamily: "'Shippori Mincho', serif" }}
                        >
                          {item.name}
                        </h3>
                        {item.desc && (
                          <p
                            className="text-white/55 text-xs md:text-sm leading-[1.9] break-words"
                            style={{ fontFamily: "'Shippori Mincho', serif" }}
                          >
                            {item.desc}
                          </p>
                        )}
                      </div>
                    </div>
                    {'priceLines' in item && item.priceLines ? (
                      <div className="flex flex-col gap-2 min-w-0 pl-12 lg:pl-0 border-t border-[#C9A84C]/10 pt-3 lg:border-t-0 lg:pt-0 lg:justify-self-end lg:w-full">
                        {item.priceLines.map((line, j) => (
                          <div
                            key={j}
                            className="grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-end gap-x-2 lg:grid-cols-[2.5rem_auto_auto] lg:justify-items-end"
                          >
                            <span
                              className="text-white/50 text-xs pb-0.5 text-left lg:text-right"
                              style={{ fontFamily: "'Shippori Mincho', serif" }}
                            >
                              {line.label}
                            </span>
                            <span
                              className="text-[#C9A84C] text-xl font-light tracking-wide leading-none text-left lg:text-right"
                              style={{ fontFamily: "'Cormorant Garamond', serif" }}
                            >
                              {line.price}
                            </span>
                            {line.unit ? (
                              <span
                                className="text-white/45 text-xs pb-0.5 text-left lg:text-right"
                                style={{ fontFamily: "'Shippori Mincho', serif" }}
                              >
                                {line.unit}
                              </span>
                            ) : (
                              <span aria-hidden="true" />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-start gap-1 min-w-0 pl-12 lg:pl-0 border-t border-[#C9A84C]/10 pt-3 lg:border-t-0 lg:pt-0 lg:items-end lg:justify-self-end lg:w-full">
                        <span
                          className="text-[#C9A84C] text-xl md:text-2xl font-light tracking-wide leading-none"
                          style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        >
                          {item.price}
                        </span>
                        {item.unit && (
                          <span
                            className="text-white/45 text-xs leading-relaxed text-left lg:text-right"
                            style={{ fontFamily: "'Shippori Mincho', serif" }}
                          >
                            {item.unit}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
